import { z } from 'zod';
const COUNTRY = /^[A-Z]{2}$/;
/** Belgian KBO/BCE: 10 digits starting with 0 or 1, the last two are 97 − (first eight mod 97). */
function belgian(digits) {
    const d = digits.length === 9 ? `0${digits}` : digits;
    if (!/^[01]\d{9}$/.test(d))
        return null;
    const check = 97 - (Number(d.slice(0, 8)) % 97);
    return check === Number(d.slice(8)) ? d : null;
}
/** Luxembourg VAT: 8 digits, the last two are the first six mod 89. */
function luxembourg(digits) {
    if (!/^\d{8}$/.test(digits))
        return null;
    return Number(digits.slice(0, 6)) % 89 === Number(digits.slice(6)) ? digits : null;
}
export function parseCompanyNumber(country, raw) {
    const cc = country.trim().toUpperCase();
    if (!COUNTRY.test(cc))
        return null;
    let body = raw.toUpperCase().replace(/[\s.\-/]/g, '');
    if (body.startsWith(cc))
        body = body.slice(cc.length);
    if (cc === 'BE') {
        const d = belgian(body);
        return d
            ? {
                key: `BE${d}`,
                country: cc,
                display: `BE ${d.slice(0, 4)}.${d.slice(4, 7)}.${d.slice(7)}`,
            }
            : null;
    }
    if (cc === 'LU') {
        const d = luxembourg(body);
        return d ? { key: `LU${d}`, country: cc, display: `LU${d}` } : null;
    }
    if (!/^[A-Z0-9]{2,20}$/.test(body))
        return null;
    return { key: `${cc}${body}`, country: cc, display: `${cc}${body}` };
}
/** Display form of a stored key. */
export function formatCompanyNumber(key) {
    return parseCompanyNumber(key.slice(0, 2), key.slice(2))?.display ?? key;
}
/** Form input `{ country, number }`, validated and normalised to a `CompanyNumber`. */
export const companyNumberInputSchema = z
    .object({
    country: z.string().trim().length(2),
    number: z.string().trim().min(2).max(32),
})
    .transform((v, ctx) => {
    const parsed = parseCompanyNumber(v.country, v.number);
    if (!parsed) {
        ctx.addIssue({ code: 'custom', path: ['number'], message: 'invalid company number' });
        return z.NEVER;
    }
    return parsed;
});
/**
 * Luxembourg trade and companies register (RCS): a section letter and its number, e.g. `B123456`.
 * Many Luxembourg clients have no VAT number; the RCS number, when they have one, identifies them.
 * Accepts the usual spellings (`RCS Luxembourg B 123.456`); null when it is not one.
 */
export function parseRcsNumber(raw) {
    const s = raw
        .toUpperCase()
        .replace(/[\s.\-/]/g, '')
        .replace(/^RCS/, '')
        .replace(/^LUXEMBOURG/, '');
    const m = /^([A-Z])0*(\d{1,7})$/.exec(s);
    return m ? `${m[1]}${m[2]}` : null;
}
/** How a client is identified on screen: VAT/enterprise number and, for Luxembourg, the RCS. */
export function formatClientNumbers(c) {
    return [
        c.companyNumber ? formatCompanyNumber(c.companyNumber) : null,
        c.rcsNumber ? `RCS ${c.rcsNumber}` : null,
    ]
        .filter(Boolean)
        .join(' · ');
}
/** Countries where a client may have no VAT number (it then gives its RCS number, if any). */
export const COUNTRIES_WITHOUT_VAT = ['LU'];
export const vatOptional = (country) => COUNTRIES_WITHOUT_VAT.includes(country.trim().toUpperCase());
/**
 * The client's identification on the registration form. A Belgian or other client gives its
 * enterprise/VAT number. A Luxembourg client may have none: both its VAT number and its RCS number
 * are optional there (the legal name then identifies it, for the checks and HubSpot).
 */
export const clientNumberInputSchema = z
    .object({
    country: z.string().trim().length(2),
    number: z.string().trim().max(32).default(''),
    rcs: z.string().trim().max(40).default(''),
})
    .transform((v, ctx) => {
    const country = v.country.toUpperCase();
    if (!COUNTRY.test(country)) {
        ctx.addIssue({ code: 'custom', path: ['country'], message: 'invalid country' });
        return z.NEVER;
    }
    let companyNumber = null;
    if (v.number) {
        companyNumber = parseCompanyNumber(country, v.number)?.key ?? null;
        if (!companyNumber) {
            ctx.addIssue({ code: 'custom', path: ['number'], message: 'invalid company number' });
        }
    }
    else if (!vatOptional(country)) {
        ctx.addIssue({ code: 'custom', path: ['number'], message: 'company number required' });
    }
    let rcsNumber = null;
    if (v.rcs) {
        rcsNumber = vatOptional(country) ? parseRcsNumber(v.rcs) : null;
        if (!rcsNumber)
            ctx.addIssue({ code: 'custom', path: ['rcs'], message: 'invalid RCS number' });
    }
    return { country, companyNumber, rcsNumber };
});
